import Checkbox from 'further-ui/components/Checkbox';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import PageFilters, { Filter } from 'components/new/PageFilters';
import { ActionOperationType, ActionRef } from 'further-types/lp/action';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { useFilters } from 'further-ui/hooks';
import { ActionRefLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';

import useSplitReconcilableTransactions, {
  ReconcilableTransaction,
} from 'hooks/lp/ui/bankReconciliation/useSplitReconcilableTransactions';
import { useRef, useState } from 'react';
import Review from './Review';

import { KeyboardDoubleArrowRightOutlined } from '@mui/icons-material';
import { Button, styled, Tooltip } from '@mui/material';
import ErrorBoundary from 'components/ErrorBoundary';
import SplitTransactionDialog from 'components/new/SplitTransactionDialog';
import { isAfter } from 'date-fns';
import { Column, Row } from 'further-ui/components/Layout';
import SearchField from 'further-ui/components/SearchField';

const SplitButton = styled(Button)({
  width: 'fit-content',
  '&[hidden]': { display: 'unset', visibility: 'hidden' },
});

type Props = {
  vehicleId: string;
  bankAccountId: string;
  preselectedTransactionId?: string;
  defaultReconcilableTransactions: Array<ReconcilableTransaction>;
  bankOperation: {
    id: string;
    spent?: number;
    received?: number;
    accountCurrency: CurrencyCode;
    date: CalendarDay;
  };
  onClose: () => void;
};

const FindAndMatch: React.FC<Props> = ({
  vehicleId,
  bankAccountId,
  preselectedTransactionId,
  bankOperation,
  onClose,
  defaultReconcilableTransactions,
}) => {
  const bankOperationAmountTcy =
    bankOperation.spent ?? bankOperation.received ?? 0;
  const [selectedTransactionIds, setSelectedTransactionIds] = useState<
    Array<string>
  >(preselectedTransactionId ? [preselectedTransactionId] : []);

  const { filters, handleChangeFilters } = useFilters(
    `find-and-match-${bankOperation.id}`,
    {
      defaultFilters: {
        pastOnly: false,
        receivedOnly: !!bankOperation.received,
        spentOnly: !!bankOperation.spent,
        searchQuery: '',
      },
    },
  );

  const {
    reconcilableTransactions,
    transactionToSplit,
    selectTransactionToSplit,
    showSplitTransactionDialog,
    closeSplitTransactionDialog,
    splitSelectedTransaction,
  } = useSplitReconcilableTransactions(
    defaultReconcilableTransactions,
    (splitFromTransactionId, newTransactionId) => {
      setSelectedTransactionIds([
        ...selectedTransactionIds.filter((id) => id !== splitFromTransactionId),
        newTransactionId,
      ]);
    },
  );

  const checkOperationType = (transaction: ReconcilableTransaction) =>
    (filters.receivedOnly &&
      transaction.operationType === ActionOperationType.Received) ||
    (filters.spentOnly &&
      transaction.operationType === ActionOperationType.Spent) ||
    (!filters.receivedOnly && !filters.spentOnly);

  const checkDate = (transaction: ReconcilableTransaction) =>
    !filters.pastOnly || transaction.ledgerDate.toUTCDate() <= new Date();

  const searchByQuery = (transaction: ReconcilableTransaction) =>
    !filters.searchQuery ||
    transaction.counterpartyLabel
      .toLowerCase()
      .includes(filters.searchQuery.toLowerCase()) ||
    transaction.unreconciledAmountTcy === Number(filters.searchQuery);

  const reconcilableTransactionsToDisplay = reconcilableTransactions
    .filter(checkOperationType)
    .filter(checkDate)
    .filter(searchByQuery)
    .map((transaction) => ({
      ...transaction,
      isSelected: selectedTransactionIds.includes(transaction.id),
    }));

  const selectedTransactions = reconcilableTransactionsToDisplay.filter(
    (transaction) => transaction.isSelected,
  );

  const selectTransactionToSplitRef = useRef(selectTransactionToSplit);
  selectTransactionToSplitRef.current = selectTransactionToSplit;

  type DisplayRow = ReconcilableTransaction & { isSelected: boolean };

  const columns: Array<TableColumn<DisplayRow>> = [
    {
      key: 'ledgerDate',
      label: 'Date',
      width: 200,
      render: (row) => {
        const isFutureDate = isAfter(
          row.ledgerDate.endOfDay(),
          bankOperation.date.endOfDay(),
        );

        return (
          <Row>
            {row.ledgerDate.toReadable()}
            {isFutureDate && (
              <Tooltip title="This action has a ledger date in the future and selecting it will generate a prepayment transaction">
                <KeyboardDoubleArrowRightOutlined fontSize="small" />
              </Tooltip>
            )}
          </Row>
        );
      },
    },
    {
      key: 'actionRef',
      label: 'Action',
      width: 200,
      render: (row) => ActionRefLabels[row.actionRef],
    },
    {
      key: 'counterpartyLabel',
      width: 200,
      label: 'Counterparty',
    },
    {
      key: 'spent',
      width: 150,
      label: 'Spent',
      render: (row) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Spent
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
    },
    {
      key: 'received',
      width: 150,
      label: 'Received',
      render: (row) =>
        numberToCurrencyString(
          row.operationType === ActionOperationType.Received
            ? row.unreconciledAmountTcy
            : undefined,
          {
            fallback: '-',
            currency: row.currencyCode,
          },
        ),
    },
    {
      key: 'split',
      label: '',
      width: 100,
      render: (row) => (
        <SplitButton
          hidden={
            !row.id ||
            !!row.splitFromTransactionId ||
            row.actionRef === ActionRef.BankAccountTransfer ||
            !row.isSelected
          }
          variant="outlined"
          size="small"
          onClick={() => {
            if (row.id) {
              selectTransactionToSplitRef.current(row.id);
            }
          }}
        >
          Split
        </SplitButton>
      ),
    },
  ];

  return (
    <Column spacing="md">
      <PageFilters>
        <Filter size="md">
          <SearchField
            autoSearchOnDebounce
            name="searchQuery"
            placeholder="Search by counterparty or amount"
            fullWidth
            onSearch={(value) => handleChangeFilters({ searchQuery: value })}
            allowEmptyQuery
            iconPlacement="right"
            value={filters.searchQuery}
          />
        </Filter>
        <Filter justify="flex-end">
          <Checkbox
            label="Show past transactions only"
            key="pastOnly"
            checked={!!filters.pastOnly}
            onChange={(event) => {
              handleChangeFilters({
                pastOnly: event.target.checked,
              });
            }}
          />
          {bankOperation.received && (
            <Checkbox
              label="Show received transactions only"
              key="receivedOnly"
              checked={!!filters.receivedOnly}
              onChange={(event) => {
                handleChangeFilters({
                  receivedOnly: event.target.checked,
                });
              }}
            />
          )}
          {bankOperation.spent && (
            <Checkbox
              label="Show spent transactions only"
              key="spentOnly"
              checked={!!filters.spentOnly}
              onChange={(event) => {
                handleChangeFilters({
                  spentOnly: event.target.checked,
                });
              }}
            />
          )}
        </Filter>
      </PageFilters>
      <Table
        scrollable
        columns={columns}
        emptyMessage="No transactions found"
        rows={reconcilableTransactionsToDisplay}
        disableSorting
        checkboxes={{
          isCheckedRow: (row) => selectedTransactionIds.includes(row.id),
          onRowCheckedToggle: (row) => {
            setSelectedTransactionIds((prev) =>
              prev.includes(row.id)
                ? prev.filter((id) => id !== row.id)
                : [...prev, row.id],
            );
          },
          allRowsChecked: reconcilableTransactionsToDisplay.every((row) =>
            selectedTransactionIds.includes(row.id),
          ),
          onCheckAllToggle: (checked) => {
            setSelectedTransactionIds(
              checked
                ? reconcilableTransactionsToDisplay.map((row) => row.id)
                : [],
            );
          },
        }}
      />
      <ErrorBoundary>
        <Review
          vehicleId={vehicleId}
          bankAccountId={bankAccountId}
          bankOperation={bankOperation}
          selectedTransactions={selectedTransactions}
          onClose={onClose}
        />
      </ErrorBoundary>
      {showSplitTransactionDialog && (
        <SplitTransactionDialog
          onClose={closeSplitTransactionDialog}
          initialSplitAmount={Math.abs(bankOperationAmountTcy)}
          transactionUnreconciledAmountTcy={
            transactionToSplit?.unreconciledAmountTcy ?? 0
          }
          onSplit={splitSelectedTransaction}
          currency={
            transactionToSplit?.currencyCode ?? bankOperation.accountCurrency
          }
        />
      )}
    </Column>
  );
};

export default FindAndMatch;
